import React, { useState, useEffect } from 'react';
import { FiGithub, FiLinkedin, FiMail } from 'react-icons/fi';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

const ROLES = [
    'Software Engineer',
    'Full-Stack Developer',
    'Self-Hosting Enthusiast',
];

const Hero = () => {
    const { t } = useTranslation();
    const [roleIndex, setRoleIndex] = useState(0);
    const [displayed, setDisplayed] = useState('');
    const [deleting, setDeleting] = useState(false);

    useEffect(() => {
        const target = ROLES[roleIndex];
        let timeout;
        if (!deleting && displayed.length < target.length) {
            timeout = setTimeout(() => setDisplayed(target.slice(0, displayed.length + 1)), 60);
        } else if (!deleting && displayed.length === target.length) {
            timeout = setTimeout(() => setDeleting(true), 2200);
        } else if (deleting && displayed.length > 0) {
            timeout = setTimeout(() => setDisplayed(displayed.slice(0, -1)), 35);
        } else if (deleting && displayed.length === 0) {
            setDeleting(false);
            setRoleIndex(i => (i + 1) % ROLES.length);
        }
        return () => clearTimeout(timeout);
    }, [displayed, deleting, roleIndex]);

    return (
        <section id="home" className="relative min-h-screen flex items-center justify-center px-5 sm:px-6 lg:px-8 overflow-hidden">
            {/* Dynamic Background Mesh (Light & Dark) */}
            <div className="absolute inset-0 z-0 pointer-events-none overflow-hidden">
                <motion.div 
                    animate={{ x: [0, 40, 0], y: [0, -30, 0] }} 
                    transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
                    className="absolute -top-[20%] -left-[10%] w-[50%] h-[50%] bg-amber-200/20 dark:bg-amber-900/20 rounded-full blur-[100px]"
                />
                <motion.div 
                    animate={{ x: [0, -40, 0], y: [0, 50, 0] }} 
                    transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut' }}
                    className="absolute top-[20%] -right-[10%] w-[40%] h-[60%] bg-blue-200/20 dark:bg-blue-900/20 rounded-full blur-[120px]"
                />
            </div>

            <div className="container-custom relative z-10">
                <div className="text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, ease: "easeOut" }}
                    >
                        {/* Avatar */}
                        <div className="mb-10 relative inline-block">
                            <motion.div 
                                className="absolute -inset-2 rounded-full bg-gradient-to-tr from-amber-400/40 to-blue-500/40 blur-xl"
                                animate={{ scale: [1, 1.1, 1], opacity: [0.5, 0.8, 0.5] }}
                                transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
                            />
                            <motion.div 
                                whileHover={{ scale: 1.05, rotate: 2 }}
                                className="relative w-32 h-32 mx-auto rounded-full overflow-hidden border-2 border-white/20 dark:border-zinc-700/50 bg-amber-50 dark:bg-zinc-800 shadow-xl backdrop-blur-sm"
                            >
                                <img
                                    src="/avatar.png"
                                    alt="Antonio Romano"
                                    className="w-full h-full object-cover"
                                    style={{ objectPosition: 'center 20%' }}
                                />
                            </motion.div>
                        </div>

                        {/* Name */}
                        <h1 className="font-display text-6xl md:text-8xl font-bold tracking-tight text-transparent bg-clip-text bg-gradient-to-r from-zinc-800 to-zinc-500 dark:from-white dark:to-zinc-400 mb-6 drop-shadow-sm">
                            {t('hero.name')}
                        </h1>

                        {/* Alias badge */}
                        <div className="flex justify-center mb-8">
                            <motion.span 
                                whileHover={{ scale: 1.05 }}
                                className="font-mono text-xs tracking-widest uppercase px-4 py-1.5 rounded-full border border-white/40 dark:border-zinc-700/50 bg-white/30 dark:bg-zinc-800/30 backdrop-blur-md text-zinc-600 dark:text-zinc-300 shadow-sm"
                            >
                                aka Rontaro
                            </motion.span>
                        </div>

                        {/* Subtitle / typewriter */}
                        <div className="mb-10 flex justify-center">
                            <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white/40 dark:bg-zinc-900/40 border border-white/50 dark:border-zinc-800/50 backdrop-blur-xl shadow-lg">
                                <span className="text-amber-500 dark:text-amber-400 font-bold">$</span>
                                <span className="font-mono text-sm md:text-base text-zinc-700 dark:text-zinc-300 min-w-[15ch] text-left">{displayed}</span>
                                <span className="inline-block w-0.5 h-4 bg-amber-500 dark:bg-amber-400 animate-pulse ml-1" />
                            </div>
                        </div>

                        {/* Description */}
                        <p className="text-base md:text-lg text-zinc-600 dark:text-zinc-400 mb-12 max-w-2xl mx-auto leading-relaxed">
                            {t('hero.description')}
                        </p>

                        {/* CTA Buttons */}
                        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-14">
                            <motion.a 
                                href="#projects" 
                                whileHover={{ scale: 1.05, y: -2 }}
                                whileTap={{ scale: 0.95 }}
                                className="btn-primary rounded-full shadow-[0_0_15px_rgba(245,158,11,0.2)] hover:shadow-[0_0_25px_rgba(245,158,11,0.4)]"
                            >
                                {t('hero.viewProjects')}
                            </motion.a>
                            <motion.a 
                                href="#contact" 
                                whileHover={{ scale: 1.05, y: -2 }}
                                whileTap={{ scale: 0.95 }}
                                className="btn-secondary rounded-full bg-white/10 dark:bg-transparent backdrop-blur-sm"
                            >
                                {t('hero.getInTouch')}
                            </motion.a>
                        </div>

                        {/* Social Links */}
                        <div className="flex justify-center gap-8">
                            {[
                                { icon: FiGithub, url: "https://github.com/Rontaro", label: "GitHub" },
                                { icon: FiLinkedin, url: "https://www.linkedin.com/in/antoromano/", label: "LinkedIn" },
                                { icon: FiMail, url: "mailto:contact@rontaro.com", label: "Email" }
                            ].map((social, idx) => (
                                <motion.a
                                    key={idx}
                                    href={social.url}
                                    target={social.label !== 'Email' ? "_blank" : "_self"}
                                    rel="noopener noreferrer"
                                    aria-label={social.label}
                                    whileHover={{ scale: 1.2, rotate: 5, color: '#f59e0b' }}
                                    whileTap={{ scale: 0.9 }}
                                    className="text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100 transition-colors p-2 rounded-full hover:bg-zinc-100 dark:hover:bg-zinc-800"
                                >
                                    <social.icon size={24} />
                                </motion.a>
                            ))}
                        </div>
                    </motion.div>
                </div>
            </div>

            {/* Scroll indicator */}
            <motion.div
                className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-zinc-400 dark:text-zinc-500 pointer-events-none"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.5, duration: 0.8 }}
            >
                <span className="font-mono text-[10px] tracking-widest uppercase">scroll</span>
                <motion.div
                    className="w-[1px] h-10 bg-gradient-to-b from-zinc-400 to-transparent dark:from-zinc-500"
                    animate={{ scaleY: [1, 0.4, 1], opacity: [0.3, 1, 0.3], originY: 0 }}
                    transition={{ repeat: Infinity, duration: 2, ease: 'easeInOut' }}
                />
            </motion.div>
        </section>
    );
};

export default Hero;
